/** Residual Split: flat or tiered plan, tier behaviour, optional reusable plan template. Separate from Schedule A. */
import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { btnGhost, btnPrimary, btnSmall, errorText, inputCls } from '../finance/financeUi';

const TIER_MODES = [['RETROACTIVE_PORTFOLIO', 'Retroactive — whole portfolio'], ['PROSPECTIVE_NEW_ACCOUNTS', 'Prospective — new accounts only']];
const DIRECTIONS = [['UP_AND_DOWN', 'Up and down'], ['UP_ONLY', 'Up only']];
const METHODS = [['CURRENT_MONTH', 'Current month'], ['TRAILING_3_MONTHS', 'Trailing 3 months'], ['YTD', 'Year to date']];
const today = () => new Date().toISOString().slice(0, 10);
const Sel = ({ value, onChange, opts, testid }) => (
  <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function TierRows({ tiers, setTiers }) {
  const upd = (i, patch) => setTiers(tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const num = (v) => (v === '' ? null : Number(v));
  return (
    <div className="space-y-2" data-testid="tier-rows">
      <div className="hidden gap-2 text-[11px] uppercase tracking-wide text-muted-foreground sm:grid sm:grid-cols-[1.4fr_1fr_1fr_0.7fr_2rem]">
        <span>Tier label</span><span>From $</span><span>Up to $</span><span>Split %</span><span />
      </div>
      {tiers.map((t, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_0.7fr_2rem] gap-2 border-b pb-2 sm:grid-cols-[1.4fr_1fr_1fr_0.7fr_2rem] sm:border-0 sm:pb-0" data-testid={`tier-row-${i}`}>
          <input className={`${inputCls} col-span-4 sm:col-span-1`} placeholder="Tier label (comp only)" value={t.label || ''} onChange={(e) => upd(i, { label: e.target.value })} data-testid={`tier-label-${i}`} />
          <input type="number" className={inputCls} placeholder="From $" value={t.min_volume ?? ''} onChange={(e) => upd(i, { min_volume: num(e.target.value) })} data-testid={`tier-min-${i}`} />
          <input type="number" className={inputCls} placeholder="and up" value={t.max_volume ?? ''} onChange={(e) => upd(i, { max_volume: num(e.target.value) })} data-testid={`tier-max-${i}`} />
          <input type="number" className={inputCls} placeholder="%" value={t.split_percent ?? ''} onChange={(e) => upd(i, { split_percent: num(e.target.value) })} data-testid={`tier-pct-${i}`} />
          <button type="button" className={`${btnSmall} justify-center px-0`} onClick={() => setTiers(tiers.filter((_, idx) => idx !== i))} aria-label="Remove tier" data-testid={`tier-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <button type="button" className={btnSmall} onClick={() => setTiers([...tiers, { min_volume: null, max_volume: null, split_percent: null }])} data-testid="tier-add-btn"><Plus className="mr-1 inline h-3.5 w-3.5" />Add tier</button>
    </div>
  );
}

const summary = (s) => {
  if (!s?.compensation_type) return '—';
  if (s.compensation_type !== 'tiered') return `Flat ${s.split_percent}%`;
  const mode = s.tier_mode === 'PROSPECTIVE_NEW_ACCOUNTS' ? 'prospective' : 'retroactive';
  return `Tiered (${(s.tiers || []).map((t) => `${t.split_percent}%`).join(' / ')}) · ${mode} · ${s.tier_direction_mode === 'UP_ONLY' ? 'up only' : 'up & down'}`;
};

export function SplitTab({ uid, split, canEdit, onSaved }) {
  const current = split?.schedule_a || {};
  const [plans, setPlans] = useState([]);
  const [f, setF] = useState({ compensation_type: current.compensation_type || 'fixed', split_percent: current.split_percent ?? '', tiers: current.tiers || [],
    tier_mode: current.tier_mode || 'RETROACTIVE_PORTFOLIO', tier_direction_mode: current.tier_direction_mode || 'UP_AND_DOWN',
    tier_volume_method: current.tier_volume_method || 'CURRENT_MONTH', plan_template_id: current.plan_template_id || '', tier_match_mode: current.tier_match_mode || 'LEGACY', effective_date: today(), reason: '' });
  useEffect(() => { api.request('/team-economics/catalog/plan-templates').then(setPlans).catch(() => setPlans([])); }, []);
  const set = (patch) => setF({ ...f, ...patch });
  const loadPlan = (id) => {
    const p = plans.find((x) => x.template_id === id);
    if (p) set({ plan_template_id: id, compensation_type: 'tiered', tiers: p.tiers.map(({ display_range, ...t }) => t), tier_mode: p.tier_mode, tier_direction_mode: p.tier_direction_mode, tier_volume_method: p.tier_volume_method, tier_match_mode: p.tier_match_mode || 'LEGACY' });
  };
  const save = async () => {
    const body = f.compensation_type === 'fixed'
      ? { compensation_type: 'fixed', split_percent: Number(f.split_percent), effective_date: f.effective_date, reason: f.reason }
      : { ...f, split_percent: null, plan_template_id: f.plan_template_id || null };
    try {
      const r = await api.request(`/team-economics/${uid}/residual-split`, { method: 'POST', body: JSON.stringify(body) });
      toast.success(r?.status === 'pending_approval' ? 'Split change sent for upline approval' : 'Residual split saved');
      onSaved();
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="space-y-4 px-5 py-4" data-testid="residual-split-tab">
      <p className="text-sm">Current: <b data-testid="current-split">{summary(current)}</b>{split?.effective_date ? ` since ${String(split.effective_date).slice(0, 10)}` : ''}</p>
      {canEdit && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {[['fixed', 'Flat revenue share'], ['tiered', 'Tiered revenue share']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set({ compensation_type: v })} data-testid={`split-type-${v}`}
                      className={`h-8 rounded-full px-3 text-xs transition-colors ${f.compensation_type === v ? 'bg-primary text-primary-foreground' : 'border hover:bg-muted'}`}>{l}</button>
            ))}
            {f.compensation_type === 'tiered' && (
              <select className={`${inputCls} sm:w-72`} value={f.plan_template_id} onChange={(e) => loadPlan(e.target.value)} data-testid="split-plan-template">
                <option value="">Start from a plan template…</option>
                {plans.map((p) => <option key={p.template_id} value={p.template_id}>{p.name}</option>)}
              </select>
            )}
          </div>
          {f.compensation_type === 'fixed' ? (
            <input type="number" min="0" max="100" className={`${inputCls} w-32`} value={f.split_percent} onChange={(e) => set({ split_percent: e.target.value })} placeholder="Split %" data-testid="split-percent-input" />
          ) : (
            <>
              <TierRows tiers={f.tiers} setTiers={(tiers) => set({ tiers })} />
              <div className="grid gap-2 sm:grid-cols-3">
                <Sel value={f.tier_mode} onChange={(v) => set({ tier_mode: v })} opts={TIER_MODES} testid="split-tier-mode" />
                <Sel value={f.tier_direction_mode} onChange={(v) => set({ tier_direction_mode: v })} opts={DIRECTIONS} testid="split-tier-direction" />
                <Sel value={f.tier_volume_method} onChange={(v) => set({ tier_volume_method: v })} opts={METHODS} testid="split-volume-method" />
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-2">
            <input type="date" className={`${inputCls} w-44`} value={f.effective_date} onChange={(e) => set({ effective_date: e.target.value })} data-testid="split-effective-date" />
            <input className={`${inputCls} sm:w-72`} value={f.reason} onChange={(e) => set({ reason: e.target.value })} placeholder="Reason" data-testid="split-reason" />
            <button type="button" className={btnPrimary} disabled={f.compensation_type === 'fixed' ? f.split_percent === '' : !f.tiers.length} onClick={save} data-testid="split-save-btn">Save split</button>
            {f.plan_template_id && <button type="button" className={btnGhost} onClick={() => set({ plan_template_id: '', tier_match_mode: 'LEGACY' })} data-testid="split-clear-template">Detach template</button>}
          </div>
        </>
      )}
      <p className="text-xs text-muted-foreground">The split is applied after Schedule A sets the eligible residual — it never changes Schedule A. Tier labels are compensation labels only, not roles or permissions. Earlier months keep the split that was in force then.</p>
    </div>
  );
}
